import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShoppingBag, Wand2, Ruler, Loader2, ArrowLeft, ExternalLink, Upload, ImagePlus, Trash2, Camera, Star, ChevronDown } from "lucide-react";
import { api, imgUrl, apiError, isPartnerListing } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatAmount, useLocale } from "@/context/LocaleContext";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import HeartButton from "@/components/HeartButton";
import ComparePrices from "@/components/ComparePrices";
import BuyRecommendation from "@/components/BuyRecommendation";
import { offersForSameItem } from "@/constants/shop";
import { AffiliateLinkNote } from "@/components/AffiliateDisclosure";

export default function ProductDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const preview = location.state?.product;
  const carried = preview && String(preview.id) === String(id) ? preview : null;
  const { user } = useAuth();
  const { formatMoney, shipsToShopper, country, currency } = useLocale();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [fit, setFit] = useState(null);
  const [fitLoading, setFitLoading] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [photoBusy, setPhotoBusy] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const replaceRef = useRef(null);
  const addRef = useRef(null);

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
    placeholderData: carried || undefined,
  });
  const product = (!isError && data) || carried;

  const { data: similar } = useQuery({
    queryKey: ["similar", id],
    enabled: isSuccess,
    queryFn: async () => (await api.get(`/products/${id}/similar`)).data,
  });

  useEffect(() => {
    setFit(null);
    setPhotoIdx(0);
    if (user) api.post("/interactions", { product_id: id, type: "view" }).catch(() => {});
    api.post("/graph/events", { type: "impression", listing_id: id, publisher: "aura-pdp" }).catch(() => {});
  }, [id, user]);

  if (!product && isLoading) {
    return <div className="max-w-[1400px] mx-auto px-6 pt-28 pb-24"><div className="h-[70vh] shimmer" /></div>;
  }
  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 pt-28 pb-24 text-center">
        <p className="text-aura-muted">This listing is not available right now.</p>
        <Link to="/shop" className="inline-block mt-6 text-sm link-underline">Back to shop</Link>
      </div>
    );
  }

  const addToCart = async () => {
    if (!user) return navigate("/auth?mode=login");
    setAdding(true);
    try {
      await api.post("/cart", { product_id: product.id, quantity: 1 });
      window.dispatchEvent(new Event("cart-updated"));
      api.post("/interactions", { product_id: product.id, type: "cart" }).catch(() => {});
      toast.success("Added to your bag");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAdding(false);
    }
  };

  const checkFit = async () => {
    if (!user) return navigate("/auth?mode=login");
    setFitLoading(true);
    try {
      const { data } = await api.get(`/fit/${product.id}`);
      setFit(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setFitLoading(false);
    }
  };

  const rawOffers = similar?.compare?.length ? similar.compare : similar?.graph?.offers;
  const sameOffers = product.source === "earnkaro" ? (rawOffers || []) : offersForSameItem(rawOffers, product);
  const sameBest = sameOffers.map((o) => Number(o?.price)).filter((n) => Number.isFinite(n) && n > 0);
  const bestSamePrice = sameBest.length ? Math.min(...sameBest) : null;

          const meta = [
    ["Brand", product.brand],
    ["Department", product.department],
    ["Product type", product.garment],
    ["SKU", product.sku],
    ["Condition", product.condition],
    ["Fabric", product.fabric],
    ["Composition", product.material_composition],
    ["Pattern", product.pattern],
    ["Fit", product.fit_type],
    ["Neckline", product.neckline],
    ["Sleeve", product.sleeve_length],
    ["Length", product.garment_length],
    ["Closure", product.closure_type],
    ["Occasion", product.occasion],
    ["Style", product.style],
    ["Colour", product.color],
    ["Size", product.size],
    ["Season", product.season],
    ["Care", product.care_instructions],
    ["Origin", product.country_of_origin],
  ].filter(([, v]) => v);

  const isExternal = product.external || isPartnerListing(product);
  const merchantUrl = product.buy_url;
  const affiliate = Boolean(product.affiliate);
  const onEbay = product.source === "ebay" || String(product.id || "").startsWith("ebay:");
  const merchantName = onEbay ? "eBay" : (product.seller_name || "merchant");
  const isOwner = Boolean(user?.id && product.seller_id === user.id && !isExternal);
  const photos = product.images?.length ? product.images : (product.image ? [product.image] : []);
  const heroSrc = photoIdx === 0 ? (product.image || photos[0]) : (photos[photoIdx] || photos[0]);
  const currentOffer = {
    id: product.id,
    source: product.source,
    seller_name: product.seller_name || product.brand || merchantName,
    price: product.price,
    currency: product.currency || "USD",
    buy_url: merchantUrl || "",
    availability: !isExternal && (product.quantity ?? 1) <= 0 ? "OutOfStock" : "InStock",
    viewing: true,
  };
  const tableOffers = sameOffers.length ? sameOffers : [currentOffer];

  const uploadPhotoFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "products");
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url || `/api/files/${data.path}`;
  };

  const savePhotos = async (images) => {
    const { data } = await api.put(`/products/${product.id}/images`, { images, prepend: false });
    queryClient.setQueryData(["product", id], data);
    queryClient.invalidateQueries({ queryKey: ["my-products"] });
    return data.images || [];
  };

  const onReplacePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoBusy("replace");
    try {
      const url = await uploadPhotoFile(file);
      const next = [...photos];
      if (!next.length) next.push(url);
      else next[photoIdx] = url;
      const saved = await savePhotos(next);
      setPhotoIdx(Math.min(photoIdx, Math.max(0, saved.length - 1)));
      toast.success("Photo replaced");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPhotoBusy("");
    }
  };

  const onAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 12) return toast.error("This listing already has 12 photos");
    setPhotoBusy("add");
    try {
      const url = await uploadPhotoFile(file);
      const saved = await savePhotos([...photos, url]);
      setPhotoIdx(Math.max(0, saved.length - 1));
      toast.success("Photo added");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPhotoBusy("");
    }
  };

  const makeShopImage = async () => {
    if (!photos.length || photoIdx === 0) return;
    setPhotoBusy("hero");
    try {
      const next = [photos[photoIdx], ...photos.filter((_, i) => i !== photoIdx)];
      await savePhotos(next);
      setPhotoIdx(0);
      toast.success("This is now the shop image");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPhotoBusy("");
    }
  };

  const removePhoto = async () => {
    if (photos.length <= 1) return toast.error("Keep at least one photo on the listing");
    setPhotoBusy("remove");
    try {
      const next = photos.filter((_, i) => i !== photoIdx);
      const saved = await savePhotos(next);
      setPhotoIdx(Math.min(photoIdx, saved.length - 1));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPhotoBusy("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-16">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-aura-muted mb-5 hover:text-aura-ink transition-colors" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_300px] gap-x-6 gap-y-3 items-stretch">
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 min-h-[240px] bg-white border border-aura-border rounded-lg overflow-hidden relative flex items-center justify-center p-3">
            {heroSrc ? (
              <img
                referrerPolicy="no-referrer"
                src={imgUrl(heroSrc)}
                alt={product.title}
                width={160}
                height={160}
                className="max-w-full max-h-[280px] w-full object-contain"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                data-testid="product-image"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-aura-muted px-4 text-center">
                <ImagePlus className="w-5 h-5" />
                <span className="text-xs">No photo</span>
              </div>
            )}
            {photoBusy && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-aura-muted" />
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto">
              {photos.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  type="button"
                  onClick={() => setPhotoIdx(i)}
                  className={`w-10 h-10 shrink-0 overflow-hidden border bg-white ${i === photoIdx ? "border-aura-accent" : "border-aura-border opacity-70 hover:opacity-100"}`}
                >
                  <img referrerPolicy="no-referrer" src={imgUrl(i === 0 && product.image ? product.image : src)} alt="" width={40} height={40} className="w-full h-full object-contain" loading="lazy" decoding="async" />
                </button>
              ))}
            </div>
          )}
          {isOwner && (
            <div className="mt-4 border border-aura-border rounded-lg p-3 bg-aura-surface" data-testid="seller-photos">
              <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-2">Your listing photos</div>
              <div className="flex flex-wrap gap-2">
                <input ref={replaceRef} type="file" accept="image/*" className="hidden" onChange={onReplacePhoto} data-testid="replace-photo-input" />
                <input ref={addRef} type="file" accept="image/*" className="hidden" onChange={onAddPhoto} data-testid="add-photo-input" />
                <button
                  type="button"
                  onClick={() => replaceRef.current?.click()}
                  disabled={Boolean(photoBusy)}
                  data-testid="replace-photo-btn"
                  className="px-3 py-2 text-xs border border-aura-border rounded-lg hover:border-aura-accent flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Upload className="w-3.5 h-3.5" /> {photos.length ? "Replace this photo" : "Upload photo"}
                </button>
                <button
                  type="button"
                  onClick={() => addRef.current?.click()}
                  disabled={Boolean(photoBusy) || photos.length >= 12}
                  data-testid="add-photo-btn"
                  className="px-3 py-2 text-xs border border-aura-border rounded-lg hover:border-aura-accent flex items-center gap-1.5 disabled:opacity-50"
                >
                  <ImagePlus className="w-3.5 h-3.5" /> Add photo
                </button>
                {photos.length > 1 && photoIdx !== 0 && (
                  <button
                    type="button"
                    onClick={makeShopImage}
                    disabled={Boolean(photoBusy)}
                    data-testid="make-shop-image-btn"
                    className="px-3 py-2 text-xs border border-aura-border rounded-lg hover:border-aura-accent flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Star className="w-3.5 h-3.5" /> Use as shop image
                  </button>
                )}
                {photos.length > 1 && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    disabled={Boolean(photoBusy)}
                    data-testid="remove-photo-btn"
                    className="px-3 py-2 text-xs border border-aura-border rounded-lg hover:border-red-300 text-aura-muted hover:text-red-500 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
                <Link
                  to={`/seller/photoshoot?listing=${product.id}`}
                  data-testid="pdp-photoshoot-link"
                  className="px-3 py-2 text-xs border border-aura-accent text-aura-accent rounded-lg hover:bg-aura-accent hover:text-white flex items-center gap-1.5"
                >
                  <Camera className="w-3.5 h-3.5" /> Studio photoshoot
                </Link>
              </div>
              <p className="mt-2 text-[11px] text-aura-muted">Shoppers see the first photo on cards. Replace the bed shot here, or generate a model shot in studio.</p>
            </div>
          )}
        </div>

        <div className="min-w-0 h-full flex flex-col justify-center">
          {product.is_live && (
            <span className="self-start inline-flex items-center gap-1.5 bg-aura-accent text-white text-[10px] tracking-luxe uppercase px-2.5 py-1 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-aura-surface animate-pulse" /> Live now
            </span>
          )}
          {product.category && (
            <div className="text-[12px] text-aura-muted mb-1">{product.category}</div>
          )}
          <h1 className="text-[16px] sm:text-[18px] font-semibold leading-snug" data-testid="product-title">{product.title}</h1>
          <div className="mt-1 text-[12px] text-aura-muted">{onEbay ? "Sold on eBay" : `Sold by ${product.seller_name}`}</div>
          {isExternal && (
            <p className="mt-1 text-[12px] text-aura-muted">
              Partner listing · checkout on {onEbay ? "ebay.com" : "the merchant site"}
            </p>
          )}
          <div className="mt-3 text-[22px] font-bold leading-none" data-testid="product-price">{formatMoney(product.price, product.currency || "USD")}</div>
          <div className="text-[12px] text-aura-muted mt-1" data-testid="product-price-listed">
            {(currency || "USD").toUpperCase() !== (product.currency || "USD").toUpperCase()
              ? `Listed at ${formatAmount(product.price, product.currency || "USD")} ${product.currency || "USD"} · shown in ${currency}`
              : `Merchant price · ${product.currency || "USD"}`}
            {product.origin_country ? ` · ships from ${product.origin_country}` : ""}
          </div>
          {!shipsToShopper(product) && (
            <div className="mt-3 text-sm text-aura-accent" data-testid="no-ship-warning">
              This piece does not ship to {country}. Choose another destination or seller.
            </div>
          )}
          {!isExternal && (
            <div className="mt-2 text-[12px] text-aura-muted" data-testid="stock-line">
              {(product.quantity ?? 1) <= 0
                ? "Sold out"
                : `${product.quantity ?? 1} in stock`}
              {(product.shipping_domestic > 0 || product.shipping_international > 0) && (
                <span>
                  {" "}· Domestic shipping fee {formatMoney(product.shipping_domestic || 0, product.currency || "USD")}
                  {" "}· International shipping fee {formatMoney(product.shipping_international || 0, product.currency || "USD")}
                </span>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-col gap-2">
            {isExternal ? (
              <a
                href={merchantUrl || "#"}
                target="_blank"
                rel={affiliate ? "sponsored noopener" : "noopener noreferrer"}
                data-testid="shop-merchant-btn"
                className="w-full px-4 py-3 btn-gradient text-[14px] font-semibold rounded-md inline-flex items-center justify-center gap-1.5"
              >
                <ExternalLink className="w-4 h-4" /> {product.source === "earnkaro" ? "Buy now" : `Shop on ${merchantName}`}
              </a>
            ) : (
              <button
                onClick={addToCart}
                disabled={adding || (product.quantity ?? 1) <= 0 || !shipsToShopper(product)}
                data-testid="add-to-cart-btn"
                className="w-full px-4 py-3 btn-gradient text-[14px] font-semibold rounded-md inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                {(product.quantity ?? 1) <= 0 ? "Sold out" : "Add to bag"}
              </button>
            )}
            {affiliate && product.source !== "earnkaro" && <AffiliateLinkNote />}
            <div className="flex gap-2">
              {!isExternal && (
                <Link to={`/tryon?product=${product.id}`} data-testid="tryon-btn" className="flex-1 px-4 py-2 btn-accent text-[13px] font-semibold rounded-md inline-flex items-center justify-center gap-1.5">
                  <Wand2 className="w-4 h-4" /> Try it on
                </Link>
              )}
              <div className="flex items-center justify-center">
                <HeartButton productId={product.id} size="w-4 h-4" className="w-9 h-9 border border-aura-border" />
              </div>
            </div>
          </div>

        </div>
        <BuyRecommendation
          product={product}
          offers={tableOffers}
          formatMoney={formatMoney}
          history={similar?.history}
        />
      </div>

      {(product.description || meta.length > 0 || !isExternal) && (
        <section className="mt-4 border border-aura-border bg-aura-surface">
          <button
            type="button"
            onClick={() => setDetailsOpen((v) => !v)}
            aria-expanded={detailsOpen}
            data-testid="product-details-toggle"
            className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 text-left"
          >
            <span className="text-[15px] font-semibold">Product details</span>
            <ChevronDown className={`w-4 h-4 text-aura-muted shrink-0 transition-transform ${detailsOpen ? "rotate-180" : ""}`} />
          </button>
          {detailsOpen && (
            <div className="px-4 sm:px-5 pb-4">
              {product.description && (
                <p className="text-[13px] leading-5 text-aura-ink/75 max-w-3xl">{product.description}</p>
              )}
              {meta.length > 0 && (
                <dl className={`${product.description ? "mt-4" : ""} border-t border-aura-border`}>
                  {meta.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] gap-3 py-2 border-b border-aura-border text-[13px]">
                      <dt className="text-aura-muted">{k}</dt>
                      <dd className="min-w-0">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {!isExternal && (
                <div className="mt-4 pt-3 border-t border-aura-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ruler className="w-4 h-4 text-aura-accent" />
                      <span className="text-[14px] font-semibold">Fit</span>
                    </div>
                    {!fit && (
                      <button onClick={checkFit} disabled={fitLoading} data-testid="check-fit-btn" className="text-[13px] link-underline flex items-center gap-2">
                        {fitLoading && <Loader2 className="w-4 h-4 animate-spin" />} Check my fit
                      </button>
                    )}
                  </div>
                  {fit && (
                    <div className="mt-3" data-testid="fit-result">
                      <div className="flex items-center justify-between text-[13px] mb-2">
                        <span>Suggested size <strong>{fit.suggested_size}</strong></span>
                        <span className="font-mono">{fit.confidence}% match</span>
                      </div>
                      <div className="h-1.5 bg-aura-sand overflow-hidden">
                        <div className="h-full bg-aura-accent" style={{ width: `${fit.confidence}%` }} />
                      </div>
                      <p className="mt-2 text-[13px] text-aura-muted">{fit.recommendation}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <div className="mt-4">
        <ComparePrices
          offers={tableOffers}
          viewingId={product.id}
          formatMoney={formatMoney}
          bestPrice={tableOffers.length > 1 ? bestSamePrice : null}
        />
      </div>

      {similar?.related?.length > 0 && (
        <div className="mt-20" data-testid="related-products">
          <h2 className="text-[16px] font-semibold mb-4">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
            {similar.related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
